import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import type { Colors } from '../../../constants/theme';
import { ROUTES } from '../../../types/global/routes.types';
import { createContest, publishContest } from '../../../redux/contest/actions';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import { replaceRouteParam } from '../../../utils/routesProcessor';
import { CONTEST_WIZARD_STEPS, CONTEST_WIZARD_TOTAL_STEPS } from './stepConfig';
import { CONTEST_WIZARD_INITIAL, type ContestAiAssistContext, type ContestWizardState } from './types';
import { clearContestWizardDraft, loadContestWizardDraft, saveContestWizardDraft } from './draftStorage';
import { plainTextFromHtml } from './richTextUtils';
import { ContestWizardFooter } from './ContestWizardFooter';
import { ContestAiAssistDialog, type ContestAiAssistResult } from './ContestAiAssistDialog';
import { buildContestPayloadFromWizard } from './buildContestPayload';
import { sanitizePhaseRows } from './dateUtils';
import { editorialHeadlineSx, glowMintBarSx, midnight } from './midnightStyles';
import { SubmissionTypeStep } from './steps/SubmissionTypeStep';
import { CampaignIdentityStep } from './steps/CampaignIdentityStep';
import { CategoryBuilderStep } from './steps/CategoryBuilderStep';
import { CategoryAwardsStep } from './steps/CategoryAwardsStep';
import { TimelinePhasesStep } from './steps/TimelinePhasesStep';
import {
    BriefRequirementsStep,
    DiscoverabilityStep,
    SubmissionRulesStep,
    VotingRatingStep,
    TechnicalSpecStep,
    JudgingFilesStep,
    ReferencesStep,
    ReviewStep,
} from './steps/ContestWizardFormSteps';
import { ContestFundingStep } from './steps/ContestFundingStep';
import { ContestQuickCreateDialog } from './ContestQuickCreateDialog';
import { ContestWizardResetConfirmDialog } from './ContestWizardResetConfirmDialog';
import {
    mergeQuickCreateDraftToWizardState,
    quickCreateDraftFromWizard,
    type ContestQuickCreateDraft,
} from './contestAutofillHelpers';

export interface MidnightContestWizardProps {
    themeColors: Colors;
}

export function MidnightContestWizard({ themeColors: _t }: MidnightContestWizardProps) {
    const dispatchPromise = useDispatchPromise();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [state, setState] = useState<ContestWizardState>(CONTEST_WIZARD_INITIAL);
    const [draftHint, setDraftHint] = useState<string | null>(null);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiContext, setAiContext] = useState<ContestAiAssistContext>('identity');
    const [submitting, setSubmitting] = useState(false);
    const [createdContestId, setCreatedContestId] = useState<string | null>(null);
    const [fundingReady, setFundingReady] = useState(false);
    const [quickCreateOpen, setQuickCreateOpen] = useState(false);
    const [quickCreateSubmitting, setQuickCreateSubmitting] = useState(false);
    const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

    const doPublish = useCallback(
        async (contestId: string) => {
            setSubmitting(true);
            try {
                await dispatchPromise(publishContest({ documentId: contestId }));
                clearContestWizardDraft();
                showToastMessage('Campaign published', 'success');
                navigate(ROUTES.BRAND_CAMPAIGNS);
            } catch {
                showToastMessage('Could not publish campaign. Please try again.', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatchPromise],
    );

    const expectCashPrizes = useMemo(() => {
        if (!state.categories_enabled) return false;
        return state.categories.some(c =>
            (c.prize_structure || []).some(p => (p.payouts || []).some(po => Number(po.prize) > 0)),
        );
    }, [state.categories, state.categories_enabled]);

    useEffect(() => {
        const id = 'midnight-editorial-fonts';
        if (!document.getElementById(id)) {
            const link = document.createElement('link');
            link.id = id;
            link.rel = 'stylesheet';
            link.href =
                'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&family=Manrope:wght@400;500;600;700&display=swap';
            document.head.appendChild(link);
        }
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [step]);

    useEffect(() => {
        const loaded = loadContestWizardDraft();
        if (loaded) {
            setState(loaded.state);
            setStep(loaded.step);
            if (loaded.createdContestId) {
                setCreatedContestId(loaded.createdContestId);
            }
            setDraftHint('Draft restored');
        }
    }, []);

    const patch = useCallback((p: Partial<ContestWizardState>) => {
        setState(prev => ({ ...prev, ...p }));
    }, []);

    const progressPct = Math.round(((step + 1) / CONTEST_WIZARD_TOTAL_STEPS) * 100);
    const meta = CONTEST_WIZARD_STEPS[step];

    const canContinue = useMemo(() => {
        switch (step) {
            case 0:
                return Boolean(state.submission_state);
            case 1:
                return state.title.trim().length > 0 && plainTextFromHtml(state.description).length > 0;
            case 2:
                return state.requirements.length > 0;
            case 5:
                if (!state.categories_enabled) return true;
                return (
                    state.categories.length > 0 &&
                    state.categories.every(
                        c => c.category_name?.trim() && plainTextFromHtml(c.category_description || ''),
                    )
                );
            case 13:
                return fundingReady;
            default:
                return true;
        }
    }, [step, state, fundingReady]);

    const handleSaveDraft = useCallback(() => {
        saveContestWizardDraft(state, step, createdContestId);
        const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        setDraftHint(`Saved at ${time}`);
        showToastMessage('Campaign draft saved', 'success');
    }, [state, step, createdContestId]);

    const handleResetForm = useCallback(() => {
        clearContestWizardDraft();
        setState(CONTEST_WIZARD_INITIAL);
        setStep(0);
        setCreatedContestId(null);
        setFundingReady(false);
        setDraftHint(null);
        setResetConfirmOpen(false);
        showToastMessage('Campaign setup reset', 'success');
    }, []);

    const handleBack = useCallback(() => {
        if (step > 0) setStep(s => s - 1);
    }, [step]);

    const handleGoToContestManage = useCallback(() => {
        if (!createdContestId) return;
        navigate(replaceRouteParam(ROUTES.BRAND_CONTEST_MANAGE, 'contestId', createdContestId));
    }, [createdContestId, navigate]);

    const handleContinue = useCallback(async () => {
        if (step < 12) {
            saveContestWizardDraft(state, step + 1, createdContestId);
            setStep(s => s + 1);
            return;
        }

        if (step === 12) {
            if (createdContestId) {
                setFundingReady(false);
                saveContestWizardDraft(state, 13, createdContestId);
                setStep(13);
                return;
            }

            setSubmitting(true);
            try {
                const payload = buildContestPayloadFromWizard(state);
                const contest = (await dispatchPromise(createContest(payload as never))) as { _id: string };
                setCreatedContestId(contest._id);
                setFundingReady(false);
                saveContestWizardDraft(state, 13, contest._id);
                setStep(13);
                showToastMessage('Campaign created — fund your prize pool to publish', 'success');
            } catch {
                showToastMessage('Could not create campaign. Please try again.', 'error');
            } finally {
                setSubmitting(false);
            }
            return;
        }

        if (step === 13 && createdContestId) {
            void doPublish(createdContestId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, state, dispatchPromise, navigate, createdContestId, doPublish]);

    const openAi = (ctx: ContestAiAssistContext) => {
        setAiContext(ctx);
        setAiOpen(true);
    };

    const [quickCreateDraft, setQuickCreateDraft] = useState(() => quickCreateDraftFromWizard(CONTEST_WIZARD_INITIAL));

    const openQuickCreate = useCallback(() => {
        setQuickCreateDraft(quickCreateDraftFromWizard(state));
        setQuickCreateOpen(true);
    }, [state]);

    const handleQuickCreate = useCallback(
        async (draft: ContestQuickCreateDraft) => {
            const merged = mergeQuickCreateDraftToWizardState(draft, state);
            setQuickCreateSubmitting(true);
            try {
                const payload = buildContestPayloadFromWizard(merged);
                const contest = (await dispatchPromise(createContest(payload as never))) as { _id: string };
                setState(merged);
                setCreatedContestId(contest._id);
                setFundingReady(false);
                setQuickCreateOpen(false);
                saveContestWizardDraft(merged, 13, contest._id);
                setStep(13);
                showToastMessage('Campaign created — fund your prize pool to publish', 'success');
            } catch {
                showToastMessage('Could not create campaign. Please try again.', 'error');
            } finally {
                setQuickCreateSubmitting(false);
            }
        },
        [state, dispatchPromise],
    );

    const applyAi = (result: ContestAiAssistResult) => {
        if (aiContext === 'identity') {
            patch({
                title: result.title ?? state.title,
                description: result.description ?? state.description,
                requirements: result.requirements?.length ? result.requirements : state.requirements,
            });
        } else if (aiContext === 'category') {
            const idx = state.editingCategoryIndex ?? 0;
            const cats = [...(state.categories.length ? state.categories : [])];
            const base = cats[idx] || {
                category_name: '',
                category_description: '',
                category_awards: [],
                with_fee: true,
                with_tiered_pricing: true,
                currency: 'USD',
                tier_fee: [],
                prize_structure: [],
            };
            cats[idx] = {
                ...base,
                category_name: result.categoryName ?? base.category_name,
                category_description: result.categoryDescription ?? base.category_description,
                tier_fee:
                    result.tiers?.map(t => ({
                        label: t.label,
                        fee: t.fee,
                        currency: 'USD',
                    })) ?? base.tier_fee,
            };
            patch({ categories: cats, editingCategoryIndex: idx });
        } else if (aiContext === 'timeline' && result.phases?.length) {
            patch({
                contest_phases_enabled: true,
                contest_phase_dates: sanitizePhaseRows(result.phases),
            });
        } else if (aiContext === 'judging' && result.score_criteria?.length) {
            patch({ score_criteria: result.score_criteria });
        } else if (aiContext === 'awards' && result.prize_structure?.length) {
            const idx = state.editingCategoryIndex ?? 0;
            const cats = [...state.categories];
            if (cats[idx]) {
                cats[idx] = { ...cats[idx], prize_structure: result.prize_structure };
                patch({ categories: cats, editingCategoryIndex: idx });
            }
        } else if (result.requirements?.length) {
            patch({ requirements: result.requirements });
        }
        showToastMessage('AI draft applied — review and edit as needed', 'info');
    };

    const renderStep = () => {
        switch (step) {
            case 0:
                return <SubmissionTypeStep state={state} onChange={patch} />;
            case 1:
                return (
                    <CampaignIdentityStep state={state} onChange={patch} onOpenAiAssist={() => openAi('identity')} />
                );
            case 2:
                return (
                    <BriefRequirementsStep
                        state={state}
                        onChange={patch}
                        onOpenAiAssist={() => openAi('requirements')}
                    />
                );
            case 3:
                return <DiscoverabilityStep state={state} onChange={patch} />;
            case 4:
                return <SubmissionRulesStep state={state} onChange={patch} />;
            case 5:
                return <CategoryBuilderStep state={state} onChange={patch} onOpenAiAssist={() => openAi('category')} />;
            case 6:
                return <CategoryAwardsStep state={state} onChange={patch} onOpenAiAssist={() => openAi('awards')} />;
            case 7:
                return <TimelinePhasesStep state={state} onChange={patch} onOpenAiAssist={() => openAi('timeline')} />;
            case 8:
                return <VotingRatingStep state={state} onChange={patch} />;
            case 9:
                return <TechnicalSpecStep state={state} onChange={patch} />;
            case 10:
                return <JudgingFilesStep state={state} onChange={patch} onOpenAiAssist={() => openAi('judging')} />;
            case 11:
                return <ReferencesStep state={state} onChange={patch} />;
            case 12:
                return <ReviewStep state={state} />;
            case 13:
                return createdContestId ? (
                    <ContestFundingStep
                        contestId={createdContestId}
                        contestTitle={state.title}
                        expectCashPrizes={expectCashPrizes}
                        wizardCategories={state.categories_enabled ? state.categories : undefined}
                        onFundingStatusChange={setFundingReady}
                    />
                ) : (
                    <Typography sx={{ color: midnight.onSurfaceVariant }}>
                        Create your campaign on the previous step to fund the prize pool.
                    </Typography>
                );
            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                position: 'relative',
                fontFamily: '"Manrope", "Inter", sans-serif',
                minHeight: 480,
            }}>
            {/* Ambient glow */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '-10%',
                    right: '-10%',
                    width: '50%',
                    height: '50%',
                    bgcolor: 'rgba(255,201,101,0.05)',
                    filter: 'blur(120px)',
                    borderRadius: '50%',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '-10%',
                    left: '-10%',
                    width: '40%',
                    height: '40%',
                    bgcolor: 'rgba(0,253,193,0.05)',
                    filter: 'blur(100px)',
                    borderRadius: '50%',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            />

            <Box sx={{ position: 'relative', zIndex: 1 }}>
                {/* Wizard header strip */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        mb: 4,
                        gap: 2,
                        flexWrap: 'wrap',
                    }}>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontWeight: 600,
                                letterSpacing: '0.2em',
                                textTransform: 'uppercase',
                                color: midnight.onSurfaceVariant,
                            }}>
                            Step {step + 1} of {CONTEST_WIZARD_TOTAL_STEPS}
                        </Typography>
                        <Typography sx={{ color: midnight.primary, fontWeight: 800, fontSize: 14 }}>
                            {meta.title}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                        <Tooltip title="Quick Contest Setup" arrow>
                            <IconButton
                                onClick={openQuickCreate}
                                disabled={submitting || quickCreateSubmitting}
                                aria-label="Quick Contest Setup"
                                sx={{
                                    color: midnight.primary,
                                    bgcolor: 'rgba(255,201,101,0.08)',
                                    border: '1px dashed rgba(255,201,101,0.35)',
                                    width: 40,
                                    height: 40,
                                    '&:hover': { bgcolor: 'rgba(255,201,101,0.18)' },
                                }}>
                                <BoltIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ ...editorialHeadlineSx, fontSize: 28, color: midnight.secondary }}>
                                {progressPct}%
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    color: midnight.onSurfaceVariant,
                                }}>
                                Setup Progress
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: '100%',
                        height: 6,
                        bgcolor: midnight.surfaceHighest,
                        borderRadius: 999,
                        overflow: 'hidden',
                        mb: 5,
                    }}>
                    <Box sx={{ ...glowMintBarSx, width: `${progressPct}%` }} />
                </Box>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={step}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.3 }}>
                        {renderStep()}
                    </motion.div>
                </AnimatePresence>

                <ContestWizardFooter
                    onBack={
                        step === 13 && createdContestId ? handleGoToContestManage : step > 0 ? handleBack : undefined
                    }
                    backLabel={step === 13 && createdContestId ? 'Go to details' : 'Back'}
                    showBackIcon={!(step === 13 && createdContestId)}
                    onSaveDraft={handleSaveDraft}
                    onResetForm={() => setResetConfirmOpen(true)}
                    onContinue={handleContinue}
                    continueLabel={
                        step === 12
                            ? createdContestId
                                ? 'Fund prize pool'
                                : submitting
                                ? 'Creating…'
                                : 'Create & fund prize pool'
                            : step === CONTEST_WIZARD_TOTAL_STEPS - 1
                            ? submitting
                                ? 'Publishing…'
                                : 'Publish campaign'
                            : `Continue to Step ${step + 2}`
                    }
                    disableContinue={!canContinue || submitting}
                    draftSavedHint={draftHint}
                />
            </Box>

            <ContestAiAssistDialog
                open={aiOpen}
                context={aiContext}
                submissionState={state.submission_state}
                awardSourceCategory={state.categories[state.editingCategoryIndex ?? 0]}
                onClose={() => setAiOpen(false)}
                onApply={applyAi}
            />

            <ContestQuickCreateDialog
                open={quickCreateOpen}
                initialDraft={quickCreateDraft}
                submitting={quickCreateSubmitting}
                onClose={() => setQuickCreateOpen(false)}
                onCreate={handleQuickCreate}
            />

            <ContestWizardResetConfirmDialog
                open={resetConfirmOpen}
                onClose={() => setResetConfirmOpen(false)}
                onConfirm={handleResetForm}
            />
        </Box>
    );
}
